import ComposeItem        from "../Entities/ComposeItem";
import HTTPService        from "@/js/Shared/Services/HTTP/HTTPService.js";
import SAPOMailConfig     from "@/SAPOMailConfig";
import EventUpload        from "@/js/Shared/Events/EventUpload";
import HTTPResponseEntity from "@/js/Shared/Services/HTTP/Entities/HTTPResponseEntity";

export {ComposeHTTPServiceSingleton as default};

/**
* Compose HTTP Service
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ComposeHTTPService {
  /**
   * Compose Enpoint
   *
   * @public
   *
   * @type {String}
   *
   * @since 7.0.0
   */
  endpoints = SAPOMailConfig.client.endpoints;

  /**
   * Calls the backend api to retrive message reply data
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} message_id
   * @param {String} folder_id
   * @param {String} type       reply type, reply_auto|reply_all|reply
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async fetchReplyData(message_id, folder_id, type) {
    let request = {
        buid   : message_id,
        view   : folder_id,
        type   : type,
        format : 'html',
    };

    return await HTTPService.oldPost(this.endpoints.imp_services + '/getReplyData', request);
  }

  /**
   * Calls the backend api to retrive message reply data
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} message_id
   * @param {String} folder_id
   * @param {String} type       forward type, forward_auto|forward_attach|editasnew
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async fetchDraftData(message_id, folder_id, type) {
    let request = {
        buid   : message_id,
        view   : folder_id,
        type   : 'resume',
        format : 'html',
    };

    return await HTTPService.oldPost(this.endpoints.imp_services + '/getResumeData', request);
  }

  /**
   * Calls the backend api to retrive message reply data
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} message_id
   * @param {String} folder_id
   * @param {String} type       forward type, forward_auto|forward_attach|editasnew
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async fetchFowardData(message_id, folder_id, type) {
    let request = {
        buid   : message_id,
        view   : folder_id,
        type   : type,
        format : 'html',
    };

    return await HTTPService.oldPost(this.endpoints.imp_services + '/ptGetForwardData', request);
  }

  /**
   * Calls the backend api to retrive a list of all contacts
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ComposeItem} compose
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async saveAutoDraft(compose) {
    return this.saveCompose(compose, 'autoSaveDraft');
  }

  /**
   * Calls the backend api to retrive a list of all contacts
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ComposeItem} compose
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async saveDraft(compose) {
    return this.saveCompose(compose, 'saveDraft');
  }

  /**
   * Calls the backend api to retrive a list of all contacts
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ComposeItem} compose
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async sendMessage(compose) {
    return this.saveCompose(compose, 'sendMessage');
  }

  /**
   * Calls the backend api to save the compose message
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ComposeItem} compose
   * @param {String}      type    sendMessage|saveDraft
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async saveCompose(compose, type) {
    let request = {
        composeCache           : compose.id,
        composeHmac            : compose.hmac,
        identity               : compose.identity_index,
        message                : compose.message,
        priority               : compose.priority,
        subject                : compose.subject,
        to                     : compose.to.getRecipients(),
        save_attachments_select: 1,
        html                   : 1,
    };

    if (compose.read_receipt) {
      request.request_read_receipt = true;
    }

    if (compose.cc.count) {
        request.cc = compose.cc.getRecipients();
    }


    if (compose.bcc.count) {
        request.bcc = compose.bcc.getRecipients();
    }

    return await HTTPService.oldPost(this.endpoints.imp_services + '/' + type, request);
  }

  /**
   * Calls the backend api to uploads an attachment file to the server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String}      compose_id compose id
   * @param {EventUpload} event      event upload
   *
   * @return {Promise<void>}
   */
  async addAttachment(compose_id, event) {
    let request = new FormData();
    request.append('MAX_FILE_SIZE', SAPOMailConfig.client.compose.attachments.size_limit);
    request.append('composeCache' , compose_id);
    request.append('json_return'  , true);
    request.append('file_id'      , event.id);
    request.append('file_upload[]', event.file);

    if (event.type === 'inline' ) {
      request.append('img_data', 1);
    }

    await HTTPService.uploadFormData(this.endpoints.imp_services + '/addAttachment', event, request);
  }

  /**
   * Calls the backend api to delete an uploaded attachment
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String}      compose_id compose id
   * @param {EventUpload} event      event upload
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async deleteAttachment(compose_id, attachment_id) {
    let request = {
      quiet       : 0,
      atc_indices :'[' + attachment_id + ']',
      imp_compose : compose_id,
    };

    return await HTTPService.oldPost(this.endpoints.imp_services + '/deleteAttach', request);
  }

  async getSapoTransferUploadData(files) {
    let names = files.items.map(file => file.name);
    return await HTTPService.post(this.endpoints.imp_services + '/transfer?op=getUploadData', {
      files: names
    });
  }

  /**
   * Calls the backend api to uploads an attachment file to the server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {EventUpload} event     event upload
   * @param {Object}      transfer transfer data
   *
   * @return {Promise<void>}
   */
  async addSapoTransferFile(event, transfer) {
    let headers = {
      'Content-type': event.file.type,
      'Accept'      : '*/*'
    };


    let blob = new Blob([event.file], { type: event.file.type });
    await HTTPService.uploadBlob(event.upload_url, event, blob, headers, 'PUT');

    // let headers                 = {};
    // let form_data               = new FormData();
    // headers['X-Csrftoken']      = transfer.token;
    // headers['X-Requested-With'] = 'XMLHttpRequest';

    // form_data.append('file', event.file);
    // await HTTPService.uploadFormData(event.upload_url, event, form_data, headers);
  }

  /**
   * Calls the backend api to uploads an attachment file to the server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Object} transfer transfer data
   *
   * @return {Promise<void>}
   */
  async signSapoTransferFile(transfer) {
    let request  = {
      id         : transfer.id,
      tokenValue : transfer.token,
      upload_id  : transfer.data.upload_id,
    };

    return await HTTPService.post(this.endpoints.imp_services + '/transfer?op=signUpload', request);
  }
}

/**
 * @type {ComposeHTTPService}
 */
const ComposeHTTPServiceSingleton = new ComposeHTTPService();